import { useState, type ReactNode } from "react";
import { Link } from "react-router";

import {
  useEquityCurve,
  useExportUrl,
  useLookups,
  useSummary,
  type EquityPoint,
  type Summary,
} from "../../api/hooks.ts";
import { usePhraseOfTheDay } from "../../api/library.ts";
import { formatPercent, formatR, formatSignedPips, toneOf } from "../../domain/format.ts";
import { EquityCurve } from "../../ui/EquityCurve.tsx";
import { Icon } from "../../ui/Icon.tsx";
import { StatusBlock } from "../../ui/StatusBlock.tsx";
import { InstrumentPips } from "../analysis/InstrumentPips.tsx";
import { PeriodsPanel } from "../analysis/PeriodsPanel.tsx";
import { usePermissions } from "../auth/permissions.ts";

/**
 * El panel.
 *
 * Manda la CURVA, no el total: un "+4R acumulado" no distingue una subida limpia
 * de una montana rusa que acabo en el mismo sitio.
 *
 * Adherencia y reglas innegociables van SEPARADAS a proposito (SPEC 16). Una
 * operacion con 9 de 10 reglas cumplidas es un 90 %, pero si la que fallo era la
 * innegociable, no es una operacion de 90 %.
 *
 * Y los PIPS, por semana y por mes, con el R al lado (ADR 0007): «ver cada
 * semana si voy bien o mal y ver cada mes como voy» es lo que el propietario
 * pidio de este panel. Cuando una suma de pips mezcla instrumentos, el `note`
 * del servidor lo dice y aqui se pinta.
 */
export function Dashboard(): ReactNode {
  const summary = useSummary();
  const equity = useEquityCurve();
  const lookups = useLookups();
  const { canWriteTrades } = usePermissions();

  return (
    <StatusBlock
      loading={summary.isPending}
      error={summary.error}
      empty={summary.data?.totalTrades === 0}
      emptyIcon="sparkles"
      emptyTitle="Todavia no hay nada que medir."
      emptyText="Registra la primera operacion y el panel empieza a contarte cosas que no se ven operando."
      emptyAction={
        canWriteTrades ? (
          <Link className="btn primary" to="/trades/new">
            <Icon name="plus" />
            Registrar una operacion
          </Link>
        ) : undefined
      }
      onRetry={() => void summary.refetch()}
    >
      {summary.data !== undefined && (
        <Panel
          data={summary.data}
          points={equity.data ?? []}
          loadingCurve={equity.isPending}
          timeZone={lookups.data?.app.timeZone}
        />
      )}
    </StatusBlock>
  );
}

function Panel(props: {
  data: Summary;
  points: readonly EquityPoint[];
  loadingCurve: boolean;
  /** La zona del SERVIDOR (SPEC 19.19): decide que semana o mes es «este». */
  timeZone: string | undefined;
}): ReactNode {
  const { data } = props;
  const exportUrl = useExportUrl();

  return (
    <>
      <div className="hello">
        <div>
          <h2>Esto es lo que llevas.</h2>
          <p>
            {data.totalTrades} operaciones registradas, {data.rated} con R y{" "}
            {data.reviewedTrades} revisadas.
          </p>
        </div>

        <PhraseCard />
      </div>

      <div className="ledger">
        <Kpi
          label="R acumulado"
          value={formatR(data.totalR)}
          tone={toneOf(data.totalR)}
          note="La suma de todos los R cerrados"
        />
        <Kpi
          label="Expectativa"
          value={formatR(data.expectancyR)}
          note="Lo que gana o pierde una operacion media"
        />
        <Kpi label="Acierto" value={formatPercent(data.winRate)} note={`${data.wins} de ${data.decided}`} />
        {/* Los pips, con el R dos casillas a la izquierda. La peor caida no se
            pierde: sigue en el panel de Riesgo, donde ya estaba tambien. */}
        <Kpi
          label="Pips"
          value={formatSignedPips(data.totalPips)}
          tone={toneOf(data.totalPips)}
          note={`En ${data.instruments} ${data.instruments === 1 ? "instrumento" : "instrumentos"}`}
        />
        <Kpi
          label="Adherencia"
          value={formatPercent(data.averageAdherence)}
          note="Reglas del plan cumplidas"
        />
        <Kpi
          label="Innegociables"
          value={String(data.brokenMandatoryRuleTrades)}
          tone={data.brokenMandatoryRuleTrades > 0 ? "breach" : undefined}
          note="Operaciones que rompieron una"
        />
      </div>

      {data.note !== null && (
        <p className="infobox" style={{ marginBottom: 16 }}>
          <b>Un pip de un instrumento no es un pip de otro.</b> {data.note} El R de al lado es la
          cifra que si se puede comparar.
        </p>
      )}

      <div className="grid" style={{ marginBottom: 16 }}>
        <PeriodsPanel timeZone={props.timeZone} className="col-8" />
        <InstrumentPips className="col-4" />
      </div>

      <div className="grid">
        <section className="panel col-8">
          <div className="panel-h">
            <div>
              <h3>Curva de equity</h3>
              <p>En R acumulados, operacion a operacion</p>
            </div>
            <span className="tag">
              <Icon name="trend" />
              {data.rated} con R
            </span>
          </div>
          <div className="panel-b">
            {props.loadingCurve ? (
              <div className="skeleton" style={{ height: 220 }} />
            ) : (
              <EquityCurve points={props.points} />
            )}
          </div>
        </section>

        <section className="panel col-4">
          <div className="panel-h">
            <div>
              <h3>Reparto</h3>
              <p>De las {data.decided} decididas</p>
            </div>
          </div>
          <div className="panel-b">
            <div className="donut-wrap">
              <Donut
                center={formatPercent(data.winRate)}
                caption="acierto"
                slices={[
                  { label: "Ganadas", value: data.wins, color: "var(--up)" },
                  { label: "Perdidas", value: data.losses, color: "var(--down)" },
                  { label: "En tablas", value: data.breakEven, color: "var(--text-3)" },
                ]}
              />
            </div>
          </div>
        </section>

        <section className="panel col-4">
          <div className="panel-h">
            <div>
              <h3>Extremos</h3>
              <p>Los bordes de tu distribucion</p>
            </div>
          </div>
          <div className="panel-b">
            <div className="stat-list">
              <Stat label="Mejor operacion" value={formatR(data.bestR)} tone={toneOf(data.bestR)} />
              <Stat label="Peor operacion" value={formatR(data.worstR)} tone={toneOf(data.worstR)} />
              <Stat label="Media ganadora" value={formatR(data.averageWinR)} />
              <Stat label="Media perdedora" value={formatR(data.averageLossR)} />
            </div>
          </div>
        </section>

        <section className="panel col-4">
          <div className="panel-h">
            <div>
              <h3>Riesgo</h3>
              <p>Lo que tuviste que aguantar</p>
            </div>
          </div>
          <div className="panel-b">
            <div className="stat-list">
              <Stat label="Pico" value={formatR(data.peakR)} />
              <Stat
                label="Peor caida"
                value={formatR(data.maxDrawdownR)}
              />
              <Stat label="Operaciones con R" value={String(data.rated)} />
              <Stat label="Sin stop, sin R" value={String(data.totalTrades - data.rated)} />
            </div>
          </div>
        </section>

        <section className="panel col-4">
          <div className="panel-h">
            <div>
              <h3>Disciplina</h3>
              <p>Habilidad, no resultado</p>
            </div>
          </div>
          <div className="panel-b stack">
            <Gauge label="Adherencia media" ratio={data.averageAdherence} />

            {data.brokenMandatoryRuleTrades > 0 ? (
              <p className="warnbox">
                <b>{data.brokenMandatoryRuleTrades}</b>{" "}
                {data.brokenMandatoryRuleTrades === 1 ? "operacion rompio" : "operaciones rompieron"}{" "}
                una regla innegociable. Es lo unico que esta interfaz senala por si solo, y mide
                disciplina, no dinero.
              </p>
            ) : (
              <p className="hint">
                Ninguna regla innegociable rota. Nueve de diez reglas cumplidas es un 90 %; si la
                que falla es la innegociable, no es una operacion de 90 %.
              </p>
            )}

            <div className="stat-list">
              <Stat label="Revisadas" value={`${data.reviewedTrades} de ${data.totalTrades}`} />
            </div>
          </div>
        </section>
      </div>

      <div className="row" style={{ marginTop: 18 }}>
        <Link className="btn ghost" to="/trades">
          <Icon name="feed" />
          Ver la bitacora
        </Link>
        <a className="btn soft" href={exportUrl}>
          <Icon name="upload" />
          Exportar a CSV
        </a>
      </div>
    </>
  );
}

/**
 * LA FRASE DEL DIA, que es donde se usan las frases que se escriben en
 * Sugerencias (migracion 0015).
 *
 * La rotacion la resuelve el SERVIDOR —`(dia del ano + offset) % tamano`— y no
 * esta pantalla: asi es la misma frase a las siete de la manana y a las once de
 * la noche, y la misma en el telefono y en el portatil. Calcularla aqui daria una
 * frase distinta por dispositivo y por hora, que es justo lo contrario de una
 * frase del dia.
 *
 * `offset` es el boton "Otra frase". Vive en el componente y no en la URL porque
 * es un gesto de un momento, no un sitio al que volver.
 *
 * SIN NINGUNA FRASE ESCRITA no se deja un hueco: se ensena la tesis del producto
 * —que es una frase de verdad, no un dato inventado— y se dice donde se escriben
 * las propias. Un bloque vacio en la esquina del panel se lee como un fallo de
 * carga.
 */
function PhraseCard(): ReactNode {
  const [offset, setOffset] = useState(0);
  const phrase = usePhraseOfTheDay(offset);

  const text = phrase.data?.text ?? null;
  const size = phrase.data?.rotationSize ?? 0;

  return (
    <div className="phrase">
      <small>{text === null ? "La tesis de esta bitacora" : "Tu frase de hoy"}</small>
      <q>{text ?? "Este journal no mide dinero. Mide habilidad."}</q>

      <div className="p-foot">
        {text === null ? (
          <Link to="/catalogos/sugerencias">Escribe las tuyas en Sugerencias</Link>
        ) : (
          <>
            {/* AL VERLAS SE ENTRA EN SU PANTALLA, que es lo que pidio el
                propietario: «al darle clic a frases se entre a una nueva pagina
                donde esten las frases para verlas de mejor manera». El rotulo
                dice cuantas rotan, que es la cuenta que importa aqui, y lleva a
                la lista entera. */}
            <Link to="/frases">
              {size === 1 ? "1 frase en rotacion" : `${size} frases en rotacion`}
            </Link>
            {/* Con una sola frase el boton daria la misma otra vez, asi que no
                se ofrece: un boton que no cambia nada ensena que la aplicacion
                no responde. */}
            {size > 1 && (
              <button
                type="button"
                disabled={phrase.isFetching}
                onClick={() => setOffset((current) => current + 1)}
              >
                <Icon name="shuffle" />
                Otra frase
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Kpi(props: {
  label: string;
  value: string;
  note: string;
  tone?: string | undefined;
}): ReactNode {
  return (
    <div className="kpi">
      <small>{props.label}</small>
      <b className={props.tone === undefined ? undefined : props.tone}>{props.value}</b>
      <span className="delta">{props.note}</span>
    </div>
  );
}

function Stat(props: { label: string; value: string; tone?: string | undefined }): ReactNode {
  return (
    <div>
      <span>{props.label}</span>
      <b className={props.tone === undefined ? undefined : props.tone}>{props.value}</b>
    </div>
  );
}

/** Una barra con su porcentaje. El vacio (`null`) no se pinta como un 0 %. */
function Gauge(props: { label: string; ratio: number | null }): ReactNode {
  const percent = props.ratio === null ? 0 : Math.round(props.ratio * 100);

  return (
    <div className="bars">
      <div className="bar">
        <span className="lbl">{props.label}</span>
        <span className="track">
          <span className="fill" style={{ width: `${percent}%` }} />
        </span>
        <span className="val">{formatPercent(props.ratio)}</span>
      </div>
    </div>
  );
}

/**
 * El donut, en SVG a mano. Cada sector es un `stroke-dasharray` sobre el mismo
 * circulo, desplazado por lo que ya llevan los anteriores.
 */
function Donut(props: {
  slices: readonly { label: string; value: number; color: string }[];
  center: string;
  caption: string;
}): ReactNode {
  const total = props.slices.reduce((sum, slice) => sum + slice.value, 0);
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  let consumed = 0;

  return (
    <>
      <div className="donut">
        <svg viewBox="0 0 120 120" width="120" height="120" aria-hidden="true">
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            style={{ stroke: "var(--surface-3)" }}
            strokeWidth="14"
          />
          {total > 0 &&
            props.slices.map((slice) => {
              const length = (slice.value / total) * circumference;
              const offset = -consumed;

              consumed += length;

              return (
                <circle
                  key={slice.label}
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="none"
                  style={{ stroke: slice.color }}
                  strokeWidth="14"
                  strokeDasharray={`${length} ${circumference - length}`}
                  strokeDashoffset={offset}
                />
              );
            })}
        </svg>
        <div className="c">
          <b>{props.center}</b>
          <small>{props.caption}</small>
        </div>
      </div>

      <div className="legend">
        {props.slices.map((slice) => (
          <div key={slice.label}>
            <i style={{ background: slice.color }} />
            {slice.label}
            <b>{slice.value}</b>
          </div>
        ))}
      </div>
    </>
  );
}
